import { useState } from "react";
import { DeductionCategory, CommissaryOrder, PayoutAdjustment } from "./types";
import { usd } from "./helpers";

interface DeductionsSectionProps {
  categories: DeductionCategory[];
  commissaryTotal: number;
  manualDeductions: PayoutAdjustment[];
  totalManualDeductions: number;
  totalDeductions: number;
  onAdd: (description: string, amountCents: number) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
}

export const DeductionsSection = ({
  categories,
  commissaryTotal,
  manualDeductions,
  totalManualDeductions,
  totalDeductions,
  onAdd,
  onDelete,
}: DeductionsSectionProps) => {
  const [showForm, setShowForm] = useState(false);
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!description.trim() || !amount) return;
    const cents = Math.round(parseFloat(amount) * 100);
    if (cents <= 0) return;

    setSaving(true);
    try {
      await onAdd(description.trim(), cents);
      setDescription("");
      setAmount("");
      setShowForm(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this deduction?")) return;
    await onDelete(id);
  };

  const hasAny =
    categories.length > 0 || manualDeductions.length > 0 || totalDeductions !== 0;

  return (
    <div className="space-y-2">
      {!hasAny && !showForm && (
        <p className="text-sm text-gray-400 py-1">No deductions.</p>
      )}

      {categories.length > 0 && (
        <div className="space-y-1">
          {categories.map((cat) => (
            <CategoryRow key={cat.categoryName} category={cat} />
          ))}
          {manualDeductions.length > 0 && (
            <div className="flex justify-between text-xs text-gray-500 pt-1">
              <span>Commissary subtotal</span>
              <span className="tabular-nums">{usd(commissaryTotal)}</span>
            </div>
          )}
        </div>
      )}

      {manualDeductions.map((d) => (
        <div
          key={d.id}
          className="flex items-center justify-between py-1.5 px-2 bg-gray-50 rounded text-sm gap-2 min-w-0"
        >
          <div className="flex items-center gap-2 min-w-0 overflow-hidden">
            <span className="text-gray-800 font-medium truncate">
              {d.description || "Deduction"}
            </span>
            <span className="text-xs text-gray-400">
              {d.created_at
                ? new Date(d.created_at).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })
                : ""}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="tabular-nums font-medium text-gray-900">
              {usd(d.amount)}
            </span>
            <button
              onClick={() => handleDelete(d.id)}
              className="text-red-500 hover:text-red-700 transition-colors"
              title="Delete"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      ))}

      {showForm && (
        <div className="space-y-2 py-1.5">
          <input
            type="text"
            placeholder="Description (e.g. Cleaning fee)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none"
          />
          <div className="flex items-center gap-2">
            <input
              type="number"
              placeholder="Amount ($)"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-28 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none"
            />
            <button
              onClick={handleSubmit}
              disabled={saving || !description.trim() || !amount}
              className="px-3 py-1.5 text-sm font-medium rounded-md bg-red-700 text-white hover:bg-red-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              onClick={() => {
                setShowForm(false);
                setDescription("");
                setAmount("");
              }}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between pt-2 border-t border-gray-300">
        <div>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="text-sm font-medium text-red-700 hover:text-red-800 transition-colors flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              Add Deduction
            </button>
          )}
          {manualDeductions.length > 0 && !showForm && (
            <div className="text-xs text-gray-500 mt-1">
              Manual deductions subtotal:{" "}
              <span className="tabular-nums">{usd(totalManualDeductions)}</span>
            </div>
          )}
        </div>
        <span className="text-sm font-semibold text-gray-900 tabular-nums">
          Total: {usd(totalDeductions)}
        </span>
      </div>
    </div>
  );
};

const CategoryRow = ({ category }: { category: DeductionCategory }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full py-1.5 px-1 text-sm hover:bg-gray-50 rounded transition-colors"
      >
        <span className="flex items-center gap-2">
          <svg
            className={`w-3 h-3 text-gray-400 transition-transform ${expanded ? "rotate-90" : ""}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
              clipRule="evenodd"
            />
          </svg>
          <span className="font-medium text-gray-800">
            {category.categoryName}
          </span>
          <span className="text-gray-500 text-xs">
            ({category.totalCount} items)
          </span>
        </span>
        <span className="tabular-nums font-medium text-gray-900">
          {usd(category.totalAmount)}
        </span>
      </button>

      {expanded && (
        <div className="ml-5 mb-2 border-l-2 border-gray-200 pl-3">
          {category.orders.map((order) => (
            <OrderRow key={order.id} order={order} />
          ))}
        </div>
      )}
    </div>
  );
};

const OrderRow = ({ order }: { order: CommissaryOrder }) => {
  const [showItems, setShowItems] = useState(false);
  const date = order.created_at
    ? new Date(order.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : "";

  return (
    <div className="py-1">
      <button
        onClick={() => setShowItems(!showItems)}
        className="flex items-center justify-between w-full text-xs hover:bg-gray-50 rounded px-1 py-0.5"
      >
        <span className="text-gray-600">
          Order #{order.id} — {date}
        </span>
        <span className="tabular-nums text-gray-800">{usd(order.total)}</span>
      </button>
      {showItems && order.items && (
        <div className="ml-3 mt-1 space-y-0.5">
          {order.items.map((item, idx) => (
            <div key={idx} className="flex justify-between text-[11px] text-gray-500 px-1">
              <span>
                {item.quantity}x {item.item_name}
              </span>
              <span className="tabular-nums">{usd(item.total_price)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
